import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Button,
  IconButton,
  TextField,
  MenuItem,
  Grid,
  Card,
  CardContent,
  LinearProgress,
  Alert
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Visibility as ViewIcon,
  Search as SearchIcon,
  FilterList as FilterIcon,
  Print as PrintIcon,
  Delete as DeleteIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import api from '../../services/api';
import { printRepairGrn } from '../../utils/repairGrnPrint';
import { canDelete } from '../../utils/auth';
import ExportButtons from '../../components/ExportButtons';
import { dash, fmtExportDate, fmtExportMoney, fmtExportStatus } from '../../utils/tableExport';

const RepairList = () => {
  const navigate = useNavigate();
  const [repairs, setRepairs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [stats, setStats] = useState(null);
  const [error, setError] = useState('');
  const allowDelete = canDelete();

  useEffect(() => {
    fetchRepairs();
    fetchStats();
  }, []);

  const fetchRepairs = async () => {
    try {
      setLoading(true);
      const response = await api.getRepairs(); // Use api.getRepairs() instead of api.get()
      if (response.success) {
        setRepairs(response.data);
      }
    } catch (error) {
      console.error('Error fetching repairs:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await api.getRepairStats(); // Use api.getRepairStats() instead of api.get()
      if (response.success) {
        setStats(response.data);
      }
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  const handleDeleteRepair = async (repair) => {
    if (!allowDelete || !repair?.id) return;
    const label = repair.grn_number || repair.service_number || repair.repair_number || `#${repair.id}`;
    if (!window.confirm(`Delete service request / JOB ${label}?\n\nThis cannot be undone.`)) return;
    try {
      setLoading(true);
      setError('');
      const res = await api.deleteRepair(repair.id);
      if (res?.success === false) {
        setError(res.message || 'Failed to delete');
        return;
      }
      await fetchRepairs();
      await fetchStats();
    } catch (err) {
      setError(err.message || err.error || 'Failed to delete service request');
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    switch(status) {
      case 'pending': return 'default';
      case 'diagnosis': return 'info';
      case 'quoted': return 'warning';
      case 'approved': return 'primary';
      case 'in_progress': return 'secondary';
      case 'completed': return 'success';
      case 'delivered': return 'success';
      case 'cancelled': return 'error';
      default: return 'default';
    }
  };

  const getPriorityColor = (priority) => {
    switch(priority) {
      case 'low': return 'success';
      case 'medium': return 'warning';
      case 'high': return 'error';
      case 'urgent': return 'error';
      default: return 'default';
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString();
  };

  const filteredRepairs = repairs.filter(repair => {
    const matchesSearch = 
      repair.grn_number?.toLowerCase().includes(search.toLowerCase()) ||
      repair.repair_number?.toLowerCase().includes(search.toLowerCase()) ||
      repair.service_number?.toLowerCase().includes(search.toLowerCase()) ||
      repair.customer_name?.toLowerCase().includes(search.toLowerCase()) ||
      repair.customer_phone?.toLowerCase().includes(search.toLowerCase()) ||
      repair.device_model?.toLowerCase().includes(search.toLowerCase()) ||
      repair.device_serial?.toLowerCase().includes(search.toLowerCase());
    
    const matchesStatus = statusFilter === 'all' || repair.status === statusFilter;
    
    return matchesSearch && matchesStatus;
  });

  if (loading) {
    return <LinearProgress />;
  }

  return (
    <Box>
      {/* Stats Overview */}
      {stats && (
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Typography color="textSecondary" gutterBottom>
                  Total Repairs
                </Typography>
                <Typography variant="h4">
                  {stats.overview?.totalRepairs || 0}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Typography color="textSecondary" gutterBottom>
                  In Progress
                </Typography>
                <Typography variant="h4" color="secondary">
                  {stats.statusBreakdown?.inProgress || 0}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Typography color="textSecondary" gutterBottom>
                  Total Revenue
                </Typography>
                <Typography variant="h4" color="success.main">
                  {stats.overview?.totalRevenue || 'LKR 0'}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Typography color="textSecondary" gutterBottom>
                  Pending Payment
                </Typography>
                <Typography variant="h4" color="error">
                  {stats.overview?.pendingPayment || 'LKR 0'}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>
      )}

      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3} flexWrap="wrap" gap={2}>
        <Typography variant="h4">Repair Management</Typography>
        <Box display="flex" gap={1} flexWrap="wrap" alignItems="center">
          <ExportButtons
            title="Service Requests / JOB"
            filename="service_requests"
            landscape
            columns={[
              { label: 'JOB #', value: (r) => dash(r.grn_number) },
              { label: 'Service #', value: (r) => dash(r.service_number || r.repair_number) },
              { label: 'Customer', value: (r) => dash(r.customer_name) },
              { label: 'Phone', value: (r) => dash(r.customer_phone) },
              { label: 'Device', value: (r) => [r.device_type, r.device_model, r.device_serial ? `(${r.device_serial})` : ''].filter(Boolean).join(' ') || '-' },
              { label: 'Received Date', value: (r) => fmtExportDate(r.received_date) },
              { label: 'Status', value: (r) => fmtExportStatus(r.status) },
              { label: 'Priority', value: (r) => fmtExportStatus(r.priority) },
              { label: 'Total', align: 'right', value: (r) => fmtExportMoney(r.total_amount) },
              { label: 'Balance', align: 'right', value: (r) => fmtExportMoney(r.balance_due) }
            ]}
            rows={filteredRepairs}
          />
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => navigate('/repairs/new')}
          >
            New Repair
          </Button>
          <Button
            variant="outlined"
            startIcon={<FilterIcon />}
            onClick={() => {/* Implement filter dialog */}}
          >
            Filters
          </Button>
        </Box>
      </Box>

      {/* Search and Filters */}
      <Box display="flex" gap={2} mb={3}>
        <TextField
          placeholder="Search by JOB #, customer name, phone..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          InputProps={{
            startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />,
          }}
          sx={{ flexGrow: 1 }}
        />
        <TextField
          select
          label="Status"
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          sx={{ minWidth: 150 }}
        >
          <MenuItem value="all">All Status</MenuItem>
          <MenuItem value="pending">Pending</MenuItem>
          <MenuItem value="diagnosis">Diagnosis</MenuItem>
          <MenuItem value="quoted">Quoted</MenuItem>
          <MenuItem value="approved">Approved</MenuItem>
          <MenuItem value="in_progress">In Progress</MenuItem>
          <MenuItem value="completed">Completed</MenuItem>
          <MenuItem value="delivered">Delivered</MenuItem>
          <MenuItem value="cancelled">Cancelled</MenuItem>
        </TextField>
      </Box>

      {/* Repairs Table */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>JOB #</TableCell>
              <TableCell>Service #</TableCell>
              <TableCell>Customer</TableCell>
              <TableCell>Phone</TableCell>
              <TableCell>Device</TableCell>
              <TableCell>Received Date</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Priority</TableCell>
              <TableCell>Total</TableCell>
              <TableCell>Balance</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredRepairs.map((repair) => (
              <TableRow key={repair.id} hover>
                <TableCell>
                  <Typography variant="body2" fontWeight="bold" color="primary" sx={{ fontFamily: 'monospace' }}>
                    {repair.grn_number || '-'}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body2" fontWeight="bold">
                    {repair.service_number || repair.repair_number}
                  </Typography>
                </TableCell>
                <TableCell>
                  {repair.customer_id ? (
                    <Button
                      size="small"
                      sx={{ textTransform: 'none', justifyContent: 'flex-start', fontWeight: 600 }}
                      onClick={() => navigate(`/repairs/customers/${repair.customer_id}`)}
                    >
                      {repair.customer_name}
                    </Button>
                  ) : (
                    repair.customer_name
                  )}
                </TableCell>
                <TableCell>{repair.customer_phone || '-'}</TableCell>
                <TableCell>
                  <Box>
                    <Typography variant="body2">{repair.device_type}</Typography>
                    <Typography variant="caption" color="textSecondary">
                      {repair.device_model} {repair.device_serial ? `(${repair.device_serial})` : ''}
                    </Typography>
                  </Box>
                </TableCell>
                <TableCell>{formatDate(repair.received_date)}</TableCell>
                <TableCell>
                  <Chip 
                    label={repair.status?.replace('_', ' ').toUpperCase()} 
                    color={getStatusColor(repair.status)}
                    size="small"
                  />
                </TableCell>
                <TableCell>
                  <Chip 
                    label={repair.priority?.toUpperCase()} 
                    color={getPriorityColor(repair.priority)}
                    size="small"
                    variant="outlined"
                  />
                </TableCell>
                <TableCell>
                  <Typography fontWeight="bold">
                    LKR {parseFloat(repair.total_amount || 0).toFixed(2)}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography color={repair.balance_due > 0 ? 'error' : 'success'}>
                    LKR {parseFloat(repair.balance_due || 0).toFixed(2)}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Box display="flex" gap={1}>
                    <IconButton
                      size="small"
                      onClick={() => navigate(`/repairs/${repair.id}`)}
                      title="View Details"
                    >
                      <ViewIcon fontSize="small" />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={() => navigate(`/repairs/edit/${repair.id}`)}
                      title="Edit"
                    >
                      <EditIcon fontSize="small" />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={() => printRepairGrn(repair)}
                      title="Print JOB Receipt"
                    >
                      <PrintIcon fontSize="small" />
                    </IconButton>
                    {allowDelete && (
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => handleDeleteRepair(repair)}
                        title="Delete (Super Admin)"
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    )}
                  </Box>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {filteredRepairs.length === 0 && (
        <Box textAlign="center" py={4}>
          <Typography color="textSecondary">
            {search || statusFilter !== 'all' ? 'No repairs found matching your criteria' : 'No repairs found'}
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default RepairList;